import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { fmt, AGENT_COLORS } from '../lib/constants'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function GoalTracker({ deals, isAdmin, isMobile, currentUser, privacyMode }) {
  const [goals, setGoals] = useState([])
  const [agents, setAgents] = useState([])
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({})

  const now = new Date()
  const currentMonth = now.getMonth()
  const currentYear = now.getFullYear()

  // Determine which agents this user can see
  const myName = currentUser?.agent_name || currentUser?.full_name || ''
  const isCreator = currentUser?.role === 'creator'

  // Load agents from profiles (scoped by RLS to current org)
  useEffect(() => {
    supabase.from('profiles').select('agent_name, role').then(({ data }) => {
      if (data) {
        const agentNames = data
          .filter(p => ['admin', 'agent'].includes((p.role || '').toLowerCase()))
          .map(p => p.agent_name)
          .filter(Boolean)
        setAgents([...new Set(agentNames)])
      }
    })
  }, [])

  // Load goals
  useEffect(() => {
    supabase.from('goals').select('*').eq('year', currentYear).then(({ data }) => {
      if (data) {
        setGoals(data)
        const form = {}
        data.forEach(g => { form[g.agent_name] = g.monthly_target || '' })
        setEditForm(form)
      }
    })
  }, [currentYear])

  // Non-admin agents only see their own row
  const visibleAgents = isAdmin ? agents : agents.filter(a => a === myName)

  // Per-agent monthly revenue (timezone-safe date comparison)
  const currentMonthStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`
  const agentStats = useMemo(() => {
    const stats = {}
    agents.forEach(agent => {
      const agentDeals = (deals || []).filter(d => d.agent === agent)
      const monthDeals = agentDeals.filter(d => {
        const dt = d.date || d.created_at
        if (!dt) return false
        // Extract YYYY-MM from the date string directly to avoid timezone shift
        const ym = String(dt).substring(0, 7)
        return ym === currentMonthStr
      })
      stats[agent] = {
        monthRevenue: monthDeals.reduce((s, d) => s + (d.deal_amount || 0), 0),
        monthDeals: monthDeals.length,
      }
    })
    return stats
  }, [deals, agents, currentMonthStr])

  // Creators see nothing
  if (isCreator) return null

  // If the logged-in agent isn't in the goals list, don't render
  if (!isAdmin && visibleAgents.length === 0) return null

  // If no goals loaded and not admin, don't show
  if (goals.length === 0 && !isAdmin) return null

  const handleSave = async () => {
    for (const agent of agents) {
      const target = Number(editForm[agent]) || 0
      const existing = goals.find(g => g.agent_name === agent && g.year === currentYear)
      if (existing) {
        await supabase.from('goals').update({ monthly_target: target, updated_at: new Date().toISOString() }).eq('id', existing.id)
      } else {
        await supabase.from('goals').insert([{ agent_name: agent, year: currentYear, monthly_target: target, org_id: currentUser?.org_id }])
      }
    }
    const { data } = await supabase.from('goals').select('*').eq('year', currentYear)
    if (data) setGoals(data)
    setEditing(false)
  }

  return (
    <div style={{
      background: 'var(--gradient-card)', border: '1px solid var(--card-border)',
      borderRadius: 'var(--radius-lg)', padding: isMobile ? 16 : 20,
      boxShadow: 'var(--shadow-xs)', position: 'relative', overflow: 'hidden'
    }}>
      <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '-0.3px' }}>
            {isAdmin ? 'Agent Goals' : 'My Goal'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {MONTHS[currentMonth]} {currentYear} · Monthly target
          </div>
        </div>
        {isAdmin && (
          <button onClick={() => setEditing(!editing)} style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 6,
            padding: '4px 10px', fontSize: 11, color: 'var(--text2)', cursor: 'pointer', fontWeight: 600
          }}>
            {editing ? 'Cancel' : 'Edit Goals'}
          </button>
        )}
      </div>

      {/* Editing form (admin only) */}
      {editing && (
        <div style={{
          display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', padding: '12px',
          background: 'var(--bg3)', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
          alignItems: 'flex-end'
        }}>
          {agents.map(agent => (
            <div key={agent} style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: AGENT_COLORS[agent] || 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{agent}</label>
              <input
                type="number" value={editForm[agent] || ''}
                onChange={e => setEditForm(prev => ({ ...prev, [agent]: e.target.value }))}
                placeholder="0"
                style={{
                  width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'var(--bg2)',
                  color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-display)', outline: 'none'
                }}
              />
            </div>
          ))}
          <button onClick={handleSave} style={{
            padding: '7px 16px', borderRadius: 6, border: 'none',
            background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', height: 34
          }}>Save</button>
        </div>
      )}

      {/* Agent progress rows — filtered by visibility */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {visibleAgents.map(agent => {
          const goal = goals.find(g => g.agent_name === agent)
          const target = goal?.monthly_target || 0
          const stat = agentStats[agent] || { monthRevenue: 0, monthDeals: 0 }
          const pct = target > 0 ? Math.min((stat.monthRevenue / target) * 100, 100) : 0
          const over = target > 0 && stat.monthRevenue > target
          const color = AGENT_COLORS[agent] || '#6b7280'

          return (
            <div key={agent} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14 }}>
              {/* Agent avatar + name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: isMobile ? 70 : 90 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${color}20`, border: `1.5px solid ${color}`, color, fontSize: 11, fontWeight: 700,
                  fontFamily: 'var(--font-display)', flexShrink: 0
                }}>{agent[0]}</div>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>{agent}</span>
              </div>

              {/* Progress bar */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, height: 7, borderRadius: 4, background: 'var(--bg3)', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 4, width: privacyMode ? '0%' : `${pct}%`,
                    background: over ? `linear-gradient(90deg, ${color}, #10b981)` : color,
                    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: pct > 60 ? `0 0 6px ${color}40` : 'none'
                  }} />
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: isMobile ? 100 : 150, justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)', color: over ? '#10b981' : 'var(--text)' }}>
                  {privacyMode ? '•••••' : fmt(stat.monthRevenue)}
                </span>
                {target > 0 ? (
                  <>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>/ {privacyMode ? '•••••' : fmt(target)}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-display)', color: over ? '#10b981' : color }}>
                      {privacyMode ? '—' : `${Math.round(pct)}%`}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>No target</span>
                )}
                <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                  ({stat.monthDeals} deal{stat.monthDeals !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
